import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Upload, FileText, Trash2, Eye, Pencil, ArrowLeft, FileX, Loader2, ShieldCheck, Download } from 'lucide-react';
import { TEMPLATE_CATEGORIES, TemplateCategory, ContractTemplate, STORAGE_KEYS, STANDARD_VARIABLES } from '@/types';
import { saveToLocalStorage, loadFromLocalStorage, generateId, readFileAsArrayBuffer, getAllTemplates, saveAllTemplates } from '@/utils/storage';
import { isAdminSession, adminLogin, adminLogout } from '@/utils/auth';
import { extractVariablesFromTemplate } from '@/utils/contractGenerator';

export default function TemplateManager() {
  const [userTemplates, setUserTemplates] = useState<ContractTemplate[]>(() => {
    return loadFromLocalStorage<ContractTemplate[]>(STORAGE_KEYS.TEMPLATES) || [];
  });
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ContractTemplate | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState<TemplateCategory>('其他');
  const [editFileData, setEditFileData] = useState<string | undefined>(undefined);
  const [editFileName, setEditFileName] = useState<string | undefined>(undefined);
  const [previewVariables, setPreviewVariables] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('全部');

  const isAdmin = isAdminSession();

  // 所有可用模板（统一从 localStorage 读取）
  const allTemplates = useMemo(() => getAllTemplates(), [userTemplates]);

  // 按分类筛选
  const filteredTemplates = useMemo(() => {
    if (selectedCategory === '全部') return allTemplates;
    return allTemplates.filter(t => t.category === selectedCategory);
  }, [allTemplates, selectedCategory]);

  // 保存用户模板列表到本地存储
  const saveUserTemplates = useCallback((newTemplates: ContractTemplate[]) => {
    setUserTemplates(newTemplates);
    saveToLocalStorage(STORAGE_KEYS.TEMPLATES, newTemplates);
  }, []);

  // 处理文件上传
  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const newTemplates: ContractTemplate[] = [...userTemplates];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.name.endsWith('.docx')) {
        alert(`文件 "${file.name}" 不是 Word 文档(.docx)，已跳过`);
        continue;
      }

      try {
        const arrayBuffer = await readFileAsArrayBuffer(file);

        // 用 JSZip 正确提取 {{变量}}
        let variables: string[] = [];
        try {
          variables = await extractVariablesFromTemplate(arrayBuffer);
        } catch (e) {
          console.warn('变量提取失败，将使用标准变量:', e);
        }

        // 将文件转为 base64 保存，生成时直接用
        const base64 = Buffer.from(arrayBuffer).toString('base64');

        const template: ContractTemplate = {
          id: generateId(),
          name: file.name.replace(/\.docx$/i, ''),
          category: '其他' as TemplateCategory,
          fileName: file.name,
          uploadTime: new Date().toISOString(),
          variables: variables.length > 0 ? variables : Object.keys(STANDARD_VARIABLES),
          fileData: base64,
        };

        newTemplates.push(template);
      } catch (error) {
        console.error('处理文件失败:', error);
        alert(`处理文件 "${file.name}" 失败，请重试`);
      }
    }

    saveUserTemplates(newTemplates);
    setUploading(false);
    setShowUpload(false);
  }, [userTemplates, saveUserTemplates]);

  // 处理拖拽
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  // 删除模板（管理员可删除所有模板，普通用户只能删除非系统模板）
  const handleDelete = useCallback((id: string, name: string) => {
    if (!window.confirm(`确定要删除模板 "${name}" 吗？此操作不可撤销。`)) return;

    const all = getAllTemplates();
    const tpl = all.find(t => t.id === id);
    if (tpl?.isSystem && !isAdmin) {
      alert('系统模板需要管理员权限才能删除');
      return;
    }

    const newAll = all.filter(t => t.id !== id);
    saveAllTemplates(newAll);
    setUserTemplates(newAll);
  }, [isAdmin]);

  // 编辑模板（管理员可编辑所有模板，普通用户只能编辑非系统模板）
  const handleEdit = useCallback((template: ContractTemplate) => {
    if (template.isSystem && !isAdmin) {
      alert('系统模板需要管理员权限才能编辑，请先登录。');
      return;
    }
    setEditingTemplate(template);
    setEditName(template.name);
    setEditCategory(template.category);
    setEditFileData(template.fileData);
    setEditFileName(undefined);
  }, [isAdmin]);

  // 保存编辑（管理员可编辑所有模板）
  const handleSaveEdit = useCallback(() => {
    if (!editingTemplate) return;
    if (editingTemplate.isSystem && !isAdmin) return;

    const all = getAllTemplates();
    const newAll = all.map(t =>
      t.id === editingTemplate.id
        ? { ...t, name: editName, category: editCategory, fileData: editFileData, fileName: editFileName || t.fileName }
        : t
    );
    saveAllTemplates(newAll);
    setUserTemplates(newAll);
    setEditingTemplate(null);
    setEditFileData(undefined);
    setEditFileName(undefined);
  }, [editingTemplate, editName, editCategory, editFileData, editFileName, isAdmin]);

  // 下载模板文件（保留变量，供用户修改后重新上传）
  const handleDownload = useCallback(async (template: ContractTemplate) => {
    try {
      let arrayBuffer: ArrayBuffer;

      if (template.fileData) {
        // 用户上传的模板：从 base64 还原
        const binaryStr = atob(template.fileData);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        arrayBuffer = bytes.buffer;
      } else if (template.systemPath) {
        // 系统预制模板：从 public/ 读取
        const resp = await fetch(template.systemPath);
        if (!resp.ok) throw new Error('无法读取系统模板文件');
        arrayBuffer = await resp.arrayBuffer();
      } else {
        alert('该模板没有关联文件，无法下载。');
        return;
      }

      const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('下载模板失败:', err);
      alert('下载失败，请重试');
    }
  }, []);

  // 预览变量
  const handlePreview = useCallback((template: ContractTemplate) => {
    setPreviewVariables(template.variables);
    setShowPreview(true);
  }, []);

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-gray-800">模板管理</h1>
          <span className="text-sm text-gray-400">共 {allTemplates.length} 个模板（{allTemplates.filter(t => t.isSystem).length} 系统 + {allTemplates.filter(t => !t.isSystem).length} 自定义）· 当前筛选：{filteredTemplates.length} 个</span>
          {isAdmin && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
              <ShieldCheck className="w-3 h-3" />
              管理员
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <button
              onClick={() => { adminLogout(); setUserTemplates([...getAllTemplates()]); }}
              className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg"
            >
              退出管理
            </button>
          ) : (
            <button
              onClick={() => {
                const pwd = prompt('请输入管理员密码：');
                if (pwd === null) return;
                if (adminLogin(pwd)) {
                  alert('管理员登录成功！');
                  setUserTemplates([...getAllTemplates()]);
                } else {
                  alert('密码错误！');
                }
              }}
              className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg"
            >
              管理员登录
            </button>
          )}
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Upload className="w-4 h-4" />
            上传自定义模板
          </button>
        </div>
      </div>

      {/* 上传区域 */}
      {showUpload && (
        <div className="bg-white rounded-xl border-2 border-dashed border-primary/30 p-8">
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-gray-300 bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">拖拽 Word 文件到此处，或点击选择文件</p>
            <p className="text-sm text-gray-400 mb-4">仅支持 .docx 格式</p>
            <input
              type="file"
              id="template-upload"
              accept=".docx"
              multiple
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            <label
              htmlFor="template-upload"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark cursor-pointer transition-colors"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  处理中...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  选择文件
                </>
              )}
            </label>
          </div>
          <button
            onClick={() => setShowUpload(false)}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            取消
          </button>
        </div>
      )}

      {/* 分类筛选 */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setSelectedCategory('全部')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            selectedCategory === '全部'
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          全部
        </button>
        {TEMPLATE_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === cat
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* 模板列表 */}
      {filteredTemplates.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <FileX className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">暂无模板</h3>
          <p className="text-gray-400 mb-6">上传您的第一个合同模板开始使用</p>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Upload className="w-4 h-4" />
            上传模板
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-sm text-gray-600">
                <tr>
                  <th className="px-6 py-3 font-medium">模板名称</th>
                  <th className="px-6 py-3 font-medium">分类</th>
                  <th className="px-6 py-3 font-medium">变量数</th>
                  <th className="px-6 py-3 font-medium">上传时间</th>
                  <th className="px-6 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-800 flex items-center gap-2">
                            {template.name}
                            {template.isSystem && (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                预制
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">{template.fileName}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {template.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {template.variables.length} 个变量
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <span className={template.isSystem ? 'text-blue-600 font-medium' : ''}>
                        {template.isSystem ? '系统预制' : new Date(template.uploadTime).toLocaleDateString('zh-CN')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handlePreview(template)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                          title="预览变量"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(template)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                          title="下载模板"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {(!template.isSystem || isAdmin) && (
                          <>
                            <button
                              onClick={() => handleEdit(template)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                              title="编辑"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(template.id, template.name)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"
                              title="删除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 编辑弹窗 */}
      {editingTemplate && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-4">编辑模板信息</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">模板名称</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">分类</label>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value as TemplateCategory)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">重新上传文件（可选）</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".docx"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const buf = await readFileAsArrayBuffer(file);
                      const base64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
                      setEditFileData(base64);
                      setEditFileName(file.name);
                    }}
                    className="text-sm text-gray-600"
                  />
                </div>
                {editFileName && (
                  <p className="text-xs text-green-600 mt-1">已选择新文件：{editFileName}</p>
                )}
                {editingTemplate.fileData && !editFileName && (
                  <p className="text-xs text-blue-600 mt-1">已替换过文件，继续保存将保留现有替换文件</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => { setEditingTemplate(null); setEditFileData(undefined); setEditFileName(undefined); }}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 变量预览弹窗 */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">模板变量列表</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            {previewVariables.length === 0 ? (
              <p className="text-gray-500 text-center py-8">未识别到变量，请在模板中使用 {"{{变量名}}"} 格式</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {previewVariables.map((variable, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-mono">
                      {variable}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 text-sm text-gray-500">
              共识别到 {previewVariables.length} 个变量
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
